import React from "react";
import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import { formatRelativeStaticPath } from "@shared/utils/images/utils";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import CardImage from "@shared/components/application/media/CardImage/CardImage.web";
import { getPriceRange } from "@shared/modules/financials/utils/price-utils";
import { NestedPublicVenueSerializer } from "@app-contexts/public/features/event-detail/data/types/events";
import { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";
import { MembershipModal } from "../memberships/MembershipModal.web";

export interface AttractionMembershipItemProps {
  membershipGroup: PublicMembershipGroupSerializer;
  venue: NestedPublicVenueSerializer;
  isLoading: boolean;
}

export const AttractionMembershipItem: React.FC<
  AttractionMembershipItemProps
> = ({ membershipGroup, venue, isLoading }) => {
  const { t } = useTranslation("purchases");
  const defaultImage = formatRelativeStaticPath("images/default-banner.png");
  const skeletonText = "skeletonText";

  const cardImage = membershipGroup?.image;

  // attraction modal variables
  const {
    isOpen: isOpenMembershipModal,
    onOpen: onOpenMembershipModal,
    onClose: onCloseMembershipModal,
  } = useDisclosure();

  const membershipName = membershipGroup?.name ?? t("membership");

  const priceRange =
    membershipGroup?.membership_levels &&
    membershipGroup.membership_levels.length > 0
      ? getPriceRange({
          items: membershipGroup.membership_levels,
          allInPricing: Boolean(venue.all_in_pricing_is_enabled),
          venueCurrency: venue.currency,
        })
      : null;

  return (
    <>
      <MembershipModal
        isOpen={isOpenMembershipModal}
        onClose={onCloseMembershipModal}
        selectedAttractionMembership={membershipGroup}
        venue={venue}
      />
      <Box
        flexGrow={2}
        width="100%"
        height="100%"
        justifyContent="center"
        p="2xs"
        // TODO: Add hover opacity effect as component variant or use _hover pseudo prop
        // eslint-disable-next-line showpass-custom/prefer-semantic-props
        css={{
          "&:hover img": {
            opacity: 0.5,
          },
        }}
        onClick={() => {
          if (!isLoading) {
            onOpenMembershipModal();
          }
        }}
      >
        <Flex
          cursor="pointer"
          direction="column"
          width="100%"
          maxWidth="100%"
          tabIndex={0}
          as="button"
          textAlign="left"
          aria-label={
            priceRange
              ? t(
                  "View details for {{membershipName}} priced at {{priceRange}}",
                  {
                    membershipName,
                    priceRange,
                  }
                )
              : t("View details for {{membershipName}}", {
                  membershipName,
                })
          }
          _focusVisible={{
            boxShadow: "outline",
            outlineOffset: "2px",
          }}
        >
          <Skeleton borderRadius="md" isLoaded={!isLoading}>
            <Flex
              borderRadius="md"
              overflow="hidden"
              width="100%"
              height="100%"
              display="block"
              aspectRatio="2 / 1"
            >
              <CardImage
                src={cardImage ?? defaultImage}
                alt={t("Membership image")}
                height={190}
                width={380}
              />
            </Flex>
          </Skeleton>
          <Skeleton isLoaded={!isLoading} mt="xs">
            <Text
              fontWeight="bold"
              fontSize={{ base: "sm", lg: "xl" }}
              color="fg"
              textOverflow="ellipsis"
              display="-webkit-box"
              overflow="hidden"
              whiteSpace="normal"
              // TODO: Create LineClamp component or add lineClamp prop to Text
              // eslint-disable-next-line showpass-custom/prefer-semantic-props
              css={{
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
              }}
            >
              {membershipGroup?.name ?? skeletonText}
            </Text>
          </Skeleton>
          <Skeleton isLoaded={!isLoading} mt="2xs" width="fit-content">
            <Text
              hidden={!venue.city}
              color="fg.muted"
              fontSize={{ base: "xs", lg: "lg" }}
            >
              {venue.city ?? skeletonText}
            </Text>
          </Skeleton>
          <Skeleton isLoaded={!isLoading} mt="lg">
            {membershipGroup?.membership_levels &&
              membershipGroup.membership_levels.length > 0 && (
                <Text fontSize={{ base: "xs", lg: "lg" }}>
                  {getPriceRange({
                    items: membershipGroup.membership_levels,
                    allInPricing: Boolean(venue.all_in_pricing_is_enabled),
                    venueCurrency: venue.currency,
                  }) ?? skeletonText}
                </Text>
              )}
          </Skeleton>
        </Flex>
      </Box>
    </>
  );
};

export default AttractionMembershipItem;
